import { useCallback, useEffect, useRef, useState } from 'react';
import { APP_VERSION } from './storage';
import { fetchPublishedVersion, withDeadline } from './updates';

type UpdateStatus = 'idle' | 'checking' | 'installing' | 'current' | 'offline' | 'failed' | 'available';

const STATUS_TEXT: Record<UpdateStatus, string> = {
  idle: '',
  checking: 'Checking for updates…',
  installing: 'Installing update… Your saved data will stay on this device.',
  current: `App is current (${APP_VERSION}).`,
  offline: 'Connect to the internet to check for an update. The app still works offline.',
  failed: 'Could not check for an update. Try again when connected.',
  available: 'Update available. Tap Update to install it.',
};

async function activateWaitingWorker(value: ServiceWorkerRegistration): Promise<boolean> {
  if (value.waiting) {
    value.waiting.postMessage({ type: 'SKIP_WAITING' });
    return true;
  }
  const worker = value.installing;
  if (!worker) return false;
  await withDeadline(new Promise<void>((resolve, reject) => {
    const changed = () => {
      if (worker.state === 'installed') resolve();
      else if (worker.state === 'redundant') reject(new Error('The update worker became redundant.'));
    };
    worker.addEventListener('statechange', changed);
    changed();
  }), 7_000);
  const waiting = value.waiting ?? (worker.state === 'installed' ? worker : null);
  waiting?.postMessage({ type: 'SKIP_WAITING' });
  return waiting !== null;
}

export function UpdateControl() {
  const [status, setStatus] = useState<UpdateStatus>('idle');
  const [busy, setBusy] = useState(false);
  const registration = useRef<ServiceWorkerRegistration | null>(null);
  const reloading = useRef(false);
  const busyRef = useRef(false);

  const checkWorker = useCallback(() => {
    void registration.current?.update().catch(() => undefined);
  }, []);

  const discoverUpdate = useCallback(async () => {
    checkWorker();
    try {
      const publishedVersion = await fetchPublishedVersion();
      if (!busyRef.current && publishedVersion !== APP_VERSION) setStatus('available');
    } catch { /* Background discovery stays quiet while offline. */ }
  }, [checkWorker]);

  useEffect(() => {
    if (status !== 'current' && status !== 'offline' && status !== 'failed') return;
    const timer = window.setTimeout(() => setStatus('idle'), 5_000);
    return () => window.clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return;
    let active = true;
    let timer: number | undefined;
    const reload = () => {
      if (reloading.current) return;
      reloading.current = true;
      window.location.reload();
    };
    const trackWorker = (worker: ServiceWorker | null) => {
      worker?.addEventListener('statechange', () => {
        if (active && worker.state === 'installed' && navigator.serviceWorker.controller) setStatus('available');
      });
    };
    const updateFound = () => trackWorker(registration.current?.installing ?? null);
    const visible = () => { if (document.visibilityState === 'visible') void discoverUpdate(); };
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).then((value) => {
      if (!active) return;
      registration.current = value;
      if (value.waiting) setStatus('available');
      trackWorker(value.installing);
      value.addEventListener('updatefound', updateFound);
      void discoverUpdate();
      timer = window.setInterval(() => void discoverUpdate(), 5 * 60 * 1000);
    }).catch(() => undefined);
    navigator.serviceWorker.addEventListener('controllerchange', reload);
    document.addEventListener('visibilitychange', visible);
    window.addEventListener('online', discoverUpdate);
    return () => {
      active = false;
      if (timer !== undefined) window.clearInterval(timer);
      registration.current?.removeEventListener('updatefound', updateFound);
      navigator.serviceWorker.removeEventListener('controllerchange', reload);
      document.removeEventListener('visibilitychange', visible);
      window.removeEventListener('online', discoverUpdate);
    };
  }, [checkWorker, discoverUpdate]);

  const update = async () => {
    if (busy) return;
    busyRef.current = true;
    setBusy(true);
    setStatus('checking');
    try {
      const publishedVersion = await withDeadline(fetchPublishedVersion(), 7_000);
      if (publishedVersion === APP_VERSION) {
        if (registration.current?.waiting || registration.current?.installing) {
          setStatus('installing');
          await activateWaitingWorker(registration.current);
        } else setStatus('current');
        return;
      }
      setStatus('installing');
      if ('serviceWorker' in navigator) {
        try {
          const activeRegistration = registration.current ?? await withDeadline(navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }), 7_000);
          registration.current = activeRegistration;
          await withDeadline(activeRegistration.update(), 7_000);
          await activateWaitingWorker(activeRegistration);
        } catch { /* A fresh navigation can still obtain the published shell. */ }
      }
      if (!reloading.current) window.location.assign(`/?update=${Date.now()}`);
    } catch {
      setStatus(navigator.onLine ? 'failed' : 'offline');
    } finally { busyRef.current = false; setBusy(false); }
  };

  if (status === 'idle' || status === 'current') return null;

  return <aside className="update-control" aria-label="App update">
    <div className="update-status" role="status" aria-live="polite">{STATUS_TEXT[status]}</div>
    <button type="button" onClick={() => void update()} disabled={busy} aria-label="Update ZYRON Voice"><span aria-hidden="true">↻</span> {busy ? (status === 'installing' ? 'Updating…' : 'Checking…') : 'Update'}</button>
  </aside>;
}
